/**
 * Client-side attachment preparation for chat.
 *
 * Images are sized against two hard transport facts:
 *  - the gateway-proxy client leg reads at most 10 MiB per frame (oversize
 *    KILLS the session — see MAX_CHAT_SEND_BYTES in terminal-core), and
 *  - openclaw inlines images ≤ 2 MB decoded on the user turn (larger ones
 *    are offloaded server-side) with a 6 MB hard cap per image.
 *
 * So: downscale/recompress to ≤ ~1.5 MB per image. Three such attachments
 * (the per-message cap) come to ~6 MB after base64 inflation — comfortably
 * inside the 8 MiB send guard.
 *
 * Non-image files (PDF + text) ride the same frame as `type: "file"` and are
 * NOT model-gated: openclaw extracts their content server-side (~60k chars
 * per file) into the agent turn for any model — best-effort, though: an
 * extraction failure silently degrades to a file path the agent opens with
 * its own tools. Office/zip formats are deliberately not offered — openclaw
 * treats them as opaque binaries the agent cannot open.
 */
import type { ChatAttachment } from '@paradigm/terminal-core';

export const MAX_ATTACHMENTS_PER_MESSAGE = 3;

// Per-file cap for non-images: the largest single file whose base64 (~4/3)
// still fits the 8 MiB frame guard (MAX_CHAT_SEND_BYTES) with envelope slack.
// openclaw itself would take 20 MB decoded — the frame is the bottleneck.
// Several large files staged together are caught by the send-time guard.
const MAX_FILE_BYTES = 6_000_000;

// Extension → MIME for the text types openclaw's extraction lane accepts.
// Browsers often report an empty File.type for these, and the wire side
// REQUIRES a real mimeType + a fileName with extension: without either,
// openclaw classifies the payload as an opaque binary and the content never
// reaches the model.
const TEXT_EXT_MIME: Readonly<Record<string, string>> = {
  md: 'text/markdown',
  markdown: 'text/markdown',
  txt: 'text/plain',
  log: 'text/plain',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  json: 'application/json',
  yaml: 'text/yaml',
  yml: 'text/yaml',
  xml: 'application/xml',
};

function fileExtension(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

/** The file-picker accept list for the current model's capabilities. */
export function buildAttachmentAccept(imagesSupported: boolean): string {
  const files =
    'application/pdf,text/*,' +
    Object.keys(TEXT_EXT_MIME)
      .map((e) => `.${e}`)
      .join(',');
  return imagesSupported ? `image/*,${files}` : files;
}

/**
 * Pre-flight size validation (file sizes are known synchronously, so this
 * runs BEFORE staging a chip). Returns a user-facing rejection or null.
 */
export function attachmentSizeError(
  file: Pick<File, 'name' | 'size'>,
  kind: 'image' | 'file',
): string | null {
  const name = file.name.length > 0 ? file.name : 'file';
  if (file.size === 0) return `${name} is empty.`;
  const max = kind === 'image' ? MAX_SOURCE_BYTES : MAX_FILE_BYTES;
  if (file.size > max) {
    return `${name} is too large (max ${Math.round(max / 1_000_000)} MB).`;
  }
  return null;
}

/**
 * How a picked/pasted/dropped file may attach on the current model:
 * 'image' | 'file' when allowed, null when not (images on a text-only model,
 * or a type we do not support at all).
 */
export function classifyAttachmentFile(
  file: Pick<File, 'name' | 'type'>,
  imagesSupported: boolean,
): 'image' | 'file' | null {
  if (file.type.startsWith('image/')) return imagesSupported ? 'image' : null;
  const ext = fileExtension(file.name);
  if (file.type === 'application/pdf' || ext === 'pdf') return 'file';
  if (file.type.startsWith('text/')) return 'file';
  if (TEXT_EXT_MIME[ext] != null) return 'file';
  return null;
}

const TARGET_MAX_BYTES = 1_500_000;
const MAX_DIMENSION = 2048;
const THUMB_DIMENSION = 192;
const THUMB_QUALITY = 0.7;
// Refuse absurd inputs outright rather than burn CPU downscaling them.
// Decimal, matching MAX_FILE_BYTES, so attachmentSizeError's "max N MB"
// message prints the same unit system for both branches (50, not 52).
const MAX_SOURCE_BYTES = 50_000_000;
// Recompression quality ladder — first rung that lands under target wins.
const JPEG_QUALITIES = [0.85, 0.7, 0.55] as const;
// Formats the model providers actually accept (Bedrock Anthropic takes
// jpeg/png/gif/webp). Anything else — bmp, tiff, heic, avif — must be
// re-encoded even when small, or the turn fails with a raw provider error.
const PASSTHROUGH_IMAGE_MIMES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.onload = () => {
      const url = reader.result as string;
      // readAsDataURL yields `data:<mime>;base64,<payload>` — the wire wants
      // the bare payload.
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}

/** Decode to something drawable, honoring EXIF orientation where supported. */
async function loadImage(
  file: File,
): Promise<{ source: CanvasImageSource; width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    return { source: bitmap, width: bitmap.width, height: bitmap.height };
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image decode failed'));
      el.src = url;
    });
    return { source: img, width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function drawScaled(
  source: CanvasImageSource,
  width: number,
  height: number,
  maxDimension: number,
): HTMLCanvasElement {
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (ctx == null) throw new Error('canvas 2d context unavailable');
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob != null ? resolve(blob) : reject(new Error('encode failed')),
      type,
      quality,
    );
  });
}

/**
 * Turn a picked/pasted/dropped file into a ready-to-send attachment, or null
 * when it can't be used (oversize, unreadable, empty).
 */
export async function processAttachmentFile(
  file: File,
  kind: 'image' | 'file',
): Promise<ChatAttachment | null> {
  return kind === 'image' ? processImageFile(file) : processGenericFile(file);
}

/** Non-image path: raw base64, no transformation, no thumbnail. */
async function processGenericFile(file: File): Promise<ChatAttachment | null> {
  if (file.size === 0 || file.size > MAX_FILE_BYTES) return null;
  const ext = fileExtension(file.name);
  // For extensions we own, the map beats the browser's guess: a Windows box
  // with Excel installed reports `.csv` as application/vnd.ms-excel, which
  // openclaw would file as an opaque Office binary instead of text.
  const mimeType =
    file.type === 'application/pdf' || ext === 'pdf'
      ? 'application/pdf'
      : TEXT_EXT_MIME[ext] || file.type || 'text/plain';
  const fileName =
    file.name.length > 0 ? file.name : `attachment.${ext || 'txt'}`;
  try {
    const content = await blobToBase64(file);
    return { type: 'file', mimeType, fileName, content };
  } catch {
    return null;
  }
}

/**
 * Image path: small originals pass through untouched (keeps PNG screenshots
 * crisp); anything over the size/dimension target is drawn down to JPEG. The
 * thumbnail data URL is what the transcript persists.
 */
async function processImageFile(file: File): Promise<ChatAttachment | null> {
  if (!file.type.startsWith('image/')) return null;
  if (file.size > MAX_SOURCE_BYTES) return null;
  try {
    const { source, width, height } = await loadImage(file);

    let mimeType = file.type;
    let content: string;
    if (
      PASSTHROUGH_IMAGE_MIMES.has(file.type) &&
      file.size <= TARGET_MAX_BYTES &&
      Math.max(width, height) <= MAX_DIMENSION
    ) {
      content = await blobToBase64(file);
    } else {
      const canvas = drawScaled(source, width, height, MAX_DIMENSION);
      mimeType = 'image/jpeg';
      let blob = await canvasToBlob(canvas, mimeType, JPEG_QUALITIES[0]);
      for (const quality of JPEG_QUALITIES.slice(1)) {
        if (blob.size <= TARGET_MAX_BYTES) break;
        blob = await canvasToBlob(canvas, mimeType, quality);
      }
      if (blob.size > TARGET_MAX_BYTES) return null;
      content = await blobToBase64(blob);
    }

    const thumbCanvas = drawScaled(source, width, height, THUMB_DIMENSION);
    const previewUrl = thumbCanvas.toDataURL('image/jpeg', THUMB_QUALITY);

    return {
      type: 'image',
      mimeType,
      ...(file.name.length > 0 ? { fileName: file.name } : {}),
      content,
      previewUrl,
    };
  } catch {
    return null;
  }
}
